import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import ApiKeys from './components/apiKeys';
import Webhooks from './components/webhooks';
import WebhookLogs from './components/webhookLogs';
import Account from './components/account';
import Orders from './components/orders';
import Order from './components/order';
import PlaceOrder from './components/placeOrder';
import OrderPlaced from './components/orderPlaced';
import Devices from './components/devices';
import Device from './components/device';
import Dashboard from './components/Dashboard';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { withAuthenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import config from './amplifyconfiguration.json';
import './App.css';
import Header from './mui-components/Header';
import { LicenseInfo } from '@mui/x-license';
import LoginHeader from './mui-components/LoginHeader';
import NotFound from './mui-components/NotFound';
import { useGlobalContext } from './context/GlobalStore';
import ContactSupport from './components/ContactSupport';
import { useNavigate } from 'react-router-dom';
import LoadingBackdrop from './mui-components/LoadingBackdrop';
import apiClient from './utils/apiClient';

LicenseInfo.setLicenseKey(
  '80ad09aa0f56ddeb00348b34abb5035eTz0xMTI1MjIsRT0xNzc4MTExOTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
);
Amplify.configure(config);
const loginComponents = {
  hideSignUp: true,
  components: {
    Header: LoginHeader,
  },
};
function App() {
  const {
    state,
    setToken,
    setApiKey,
    setAccount,
    setIsDeviceLoading,
    setIsOrderLoading,
    setDevices,
    setOrders,
    setIsloading,
  } = useGlobalContext();
  const navigate = useNavigate();

  useEffect(() => {
    getSession();
  }, []);

  async function getSession() {
    setIsloading(false);
    try {
      const { tokens, credentials, identityId, userSub } =
        await fetchAuthSession();
      const { idToken, accessToken } = tokens;
      setToken(accessToken);
      apiClient.setAuthTokens(idToken, accessToken);
      const account_id = await getAccount();
      getDevices(account_id);
      getOrders(account_id);
    } catch (error) {
      console.log('error', error);
      navigate('/contact-support');
      setIsloading(false);
    }
  }

  async function getAccount() {
    try {
      const response = await apiClient.getAccount();
      setAccount(response);
      let keyName = "";
      let key = "";
      let key_id = "";
      if (localStorage.getItem('apiKeyName')) {
        keyName = localStorage.getItem('apiKeyName');
        const storedAccount = response.pylo_accounts.find((a) => a.name === keyName);
        if (!storedAccount) {
          keyName = response.pylo_accounts[0].name;
          key = response.pylo_accounts[0].api_key;
          key_id = response.pylo_accounts[0].key_id;
          localStorage.setItem('apiKeyName', keyName);
        } else {
          key = storedAccount.api_key;
          key_id = storedAccount.key_id;
        }
      } else {
        keyName = response.pylo_accounts[0].name;
        key = response.pylo_accounts[0].api_key;
        key_id = response.pylo_accounts[0].key_id;
        localStorage.setItem('apiKeyName', keyName);
      }

      setApiKey({
        name: keyName,
        key: key,
        key_id: key_id
      });

      return response.pylo_accounts.find((a) => a.name === keyName)['id'];
    } catch (error) {
      console.log('error', error);
      navigate('/contact-support');
      setIsloading(false);
    }
  }

  async function getDevices(accountId) {
    try {
      const response = await apiClient.getDevices(accountId);
      setDevices(response);
    } catch (error) {
      console.log('error', error);
      setIsloading(false);
    } finally {
      setIsDeviceLoading(false);
    }
  }

  async function getOrders(accountId) {
    try {
      const response = await apiClient.getOrders(accountId);
      const sorted = sortedOrders(response);
      setOrders(sorted);
    } catch (error) {
      console.error({ error });
    } finally {
      setIsOrderLoading(false);
    }
  }

  const sortedOrders = (responseData) => {
    const sortedOrders = [];
    const dates = new Set(responseData.map((o) => o.date_placed));
    const sortedDates = Array.from(dates).sort().reverse();
    sortedDates.forEach((d) => {
      responseData
        .filter((o) => o.date_placed === d)
        .forEach((o) => {
          sortedOrders.push(o);
        });
    });
    return sortedOrders;
  };
  const signOutAndRedirect = async () => {
    localStorage.setItem('pmpAuthStatus', Date.now().toString());
    apiClient.post('/signOut', {}, {}, true);
    signOut({ global: true });
    apiClient.setAuthTokens(null, null);
    setToken(null);
    navigate('/');
  };

  useEffect(() => {
    // if both vals are true then toggle isloading
    if (!state.isDeviceLoading && !state.isOrderLoading) {
      setIsloading(false);
    }
  }, [state.isDeviceLoading, state.isOrderLoading]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'pmpAuthStatus') {
        apiClient.setAuthTokens(null, null);
        setToken(null);
        signOut({ global: true });
        navigate('/');
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Clean up
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, setToken, signOut])

  return (
    <div className="App">
      <LoadingBackdrop />
      <Header signOut={signOutAndRedirect} />
      <Routes>
        <Route exact path="/contact-support" element={<ContactSupport />} />
        <Route
          exact
          path="/orders"
          element={
            <Orders
              getOrders={getOrders}
            />
          }
        />
        <Route path="/orders/:id" element={<Order />} />
        <Route
          exact
          path="/placeOrder"
          element={<PlaceOrder setOrders={setOrders} />}
        />
        <Route path="/placeOrder/:id" element={<OrderPlaced />} />
        <Route exact path="/" element={<Dashboard />} />
        <Route exact path="/devices" element={<Devices getDevices={getDevices} />} />
        <Route path="/devices/:id" element={<Device />} />
        {/* <Route path="/readings/:id" element={<Readings token={token} />} /> */}
        <Route exact path="/apiKeys" element={<ApiKeys getAccount={getAccount}/>} />
        <Route exact path="/webhooks" element={<Webhooks getAccount={getAccount}/>} />
        <Route path="/webhooks/:eventType/logs" element={<WebhookLogs />} />
        <Route
          exact
          path="/account"
          element={<Account getAccount={getAccount} />}
        />
        <Route path="*" exact={true} element={<NotFound />} />
      </Routes>
    </div>
  );
}

export default withAuthenticator(App, loginComponents);
